import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiArrowRight, 
  FiTrendingUp, 
  FiUsers, 
  FiTarget,
  FiStar,
  FiPlay 
} from 'react-icons/fi';
import { Image, BackgroundImage, HeroImage } from '../components/ui/Image';
import { imageConfig } from '../config/images';
import { companyStats } from '../config/stats';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 60 },
  animate: { opacity: 1, y: 0 }
};

const fadeInUpTransition = { duration: 0.6, ease: "easeOut" };

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const AnimatedNumber: React.FC<{ value: number; suffix?: string; duration?: number }> = ({ value, suffix = '%', duration = 1400 }) => {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    let start: number | null = null;
    let frame: number;

    const step = (timestamp: number) => {
      if (start === null) start = timestamp;
      const progress = Math.min((timestamp - start) / duration, 1);
      setDisplay(Math.round(progress * value));
      if (progress < 1) {
        frame = requestAnimationFrame(step);
      }
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value, duration]);

  return <span>{display}{suffix}</span>;
};

export const HomePage: React.FC = () => {
  useDocumentTitle(
    'Best Digital Marketing Agency in India | Grow with Trusted Experts',
    'Get result driven best SEO expert, Google Ads, Meta Ads, and full digital marketing services from one of the best digital marketing agencies in India. Simple process, clear results, and support you can trust.'
  );

  const stats = [
    { number: '150+', label: 'Brands Supported', icon: FiUsers },
    { number: '4.9', label: 'Client Rating', icon: FiStar },
    { number: '12+', label: 'Countries Served', icon: FiTrendingUp },
    { number: '10M+', label: 'Leads Generated', icon: FiTarget },
  ];

  const services = [
    {
      title: 'Search Engine Optimization (SEO)',
      description: 'Rank higher on search engines and attract high-intent customers with Traditional SEO and AI-powered optimization.',
      features: ['Traditional SEO', 'AI SEO', 'Keyword Research', 'Technical SEO Audit'],
      href: '/services/seo-services',
    },
    {
      title: 'Google Ads Management',
      description: 'Get leads and sales through expert Google Ads campaign management and optimization.',
      features: ['Search Ads', 'Display Network', 'Shopping Ads', 'Remarketing'],
      href: '/services/google-ads',
    },
    {
      title: 'Meta Ads (Facebook & Instagram)',
      description: 'Bring more inquiries and buyers with targeted social media ad campaigns.',
      features: ['Facebook Ads', 'Instagram Ads', 'Lead Generation', 'Creative Design'],
      href: '/services/meta-ads',
    },
    {
      title: 'Social Media Marketing',
      description: 'Content planning, posting, messaging, and audience growth across all platforms.',
      features: ['Facebook Marketing', 'Instagram Marketing', 'LinkedIn Marketing', 'Twitter Marketing'],
      href: '/services/social-media',
    },
    {
      title: 'YouTube Advertising',
      description: 'Engage viewers with compelling video ads on the world\'s second-largest search engine.',
      features: ['Video Ads', 'Bumper Ads', 'Discovery Ads', 'Video Production'],
      href: '/services/youtube-ads',
    },
    {
      title: 'Bing Ads Management',
      description: 'Tap into Microsoft\'s search network with cost-effective advertising campaigns.',
      features: ['Bing Search Ads', 'Microsoft Audience Network', 'LinkedIn Targeting', 'Lower CPC'],
      href: '/services/bing-ads',
    },
    {
      title: 'Web Development',
      description: 'Conversion-focused websites and sales funnels built for growth.',
      features: ['Responsive Design', 'E-commerce', 'Landing Pages', 'CMS Integration'],
      href: '/services/web-design',
    },
  ];

  const testimonials = [
    {
      name: 'Dr. Nisharg Patel',
      company: 'Gastroenterologists',
      quote: 'Mirani Marketing significantly improved our search visibility and patient inquiries. Our website rankings increased steadily within months, bringing consistent organic growth.',
      avatar: imageConfig.testimonials.client1
    },
    {
      name: 'Nasim',
      company: 'NooriTalk',
      quote: 'Their SEO and Google Ads strategy helped us scale faster than expected. We saw better traffic quality, stronger conversions, and a clear return on investment.',
      avatar: imageConfig.testimonials.client2
    },
    {
      name: 'Aman Memon',
      company: 'Smart Fashion',
      quote: 'Mirani Marketing\'s performance marketing approach boosted our online sales and brand reach. Their optimization and targeting made a real difference to our revenue.',
      avatar: imageConfig.testimonials.client3
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section - WAM Style */}
      <section className="relative bg-gradient-to-br from-primary-600 to-primary-700 text-white overflow-hidden py-6 md:py-10">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=1920&h=1080&fit=crop" 
            alt="Modern office workspace"
            className="w-full h-full object-cover"
          />
          {/* Multi-layer gradient overlay for depth - Reduced opacity */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary-600/50 via-primary-700/45 to-primary-800/50"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-primary-900/25 via-transparent to-transparent"></div>
          {/* Subtle pattern overlay */}
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '32px 32px'
          }}></div>
        </div>
        
        <div className="relative container-width">
          <motion.div 
            className="max-w-5xl mx-auto"
            variants={staggerContainer}
            initial="initial"
            animate="animate"
          >
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Left Content */}
              <div className="text-left relative">
                {/* 3D Bubble 1 - #52658C - Behind text */}
                <div 
                  className="absolute -top-12 -left-16 w-40 h-40 md:w-48 md:h-48 rounded-full animate-pulse pointer-events-none z-0"
                  style={{
                    background: 'radial-gradient(circle at 30% 30%, #7a93c3, #52658C 50%, #3d4d6b)',
                    boxShadow: '0 20px 60px rgba(82, 101, 140, 0.5), inset -10px -10px 20px rgba(0, 0, 0, 0.3), inset 10px 10px 20px rgba(255, 255, 255, 0.2)',
                    border: '1px solid rgba(255, 255, 255, 0.1)'
                  }}
                ></div>
                
                {/* 3D Bubble 2 - Darker Blue - Near "Solutions" text */}
                <div 
                  className="absolute top-32 left-20 w-28 h-28 md:w-32 md:h-32 rounded-full animate-bounce pointer-events-none z-0"
                  style={{
                    background: 'radial-gradient(circle at 30% 30%, #3d5a80, #2a3f5f 50%, #1a2537)',
                    boxShadow: '0 15px 45px rgba(42, 63, 95, 0.6), inset -8px -8px 16px rgba(0, 0, 0, 0.4), inset 8px 8px 16px rgba(255, 255, 255, 0.15)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    animationDelay: '0.5s'
                  }}
                ></div>
                
                <motion.h1 
                  className="text-4xl md:text-6xl font-bold mb-4 leading-tight relative z-10"
                  variants={fadeInUp}
                  transition={fadeInUpTransition}
                  style={{
                    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  Top Digital Marketing
                  <span className="block text-white">
                    Company
                  </span>
                </motion.h1>
                
                <motion.p 
                  className="text-lg md:text-xl mb-6 leading-relaxed max-w-2xl relative z-10"
                  variants={fadeInUp}
                  transition={fadeInUpTransition}
                  style={{
                    color: '#FFFFFF',
                    textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)'
                  }}
                >
                  We help businesses grow through our best digital marketing services. Brands trust us as one of the best digital marketing agencies because we focus on real results, not just clicks.
                </motion.p>
                
                <motion.div 
                  className="flex flex-row gap-4 items-center flex-wrap sm:flex-nowrap justify-center sm:justify-start"
                  variants={fadeInUp}
                  transition={fadeInUpTransition}
                >
                  <Link
                    to="/contact"
                    className="group relative bg-white text-primary-600 px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 overflow-hidden whitespace-nowrap"
                    style={{
                      boxShadow: '0 8px 24px rgba(255, 255, 255, 0.3), inset -2px -2px 8px rgba(0, 0, 0, 0.1), inset 2px 2px 8px rgba(255, 255, 255, 0.8)'
                    }}
                  >
                    {/* Animated background on hover */}
                    <span className="absolute inset-0 bg-gradient-to-r from-white/20 via-transparent to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                    
                    {/* Button content */}
                    <span className="relative z-10 flex items-center group-hover:scale-110 transition-transform duration-300">
                      Start Your Project
                      <FiArrowRight className="ml-2 w-5 h-5 inline group-hover:translate-x-1 transition-transform duration-300" />
                    </span>
                    
                    {/* 3D shine effect */}
                    <span className="absolute inset-0 rounded-full bg-gradient-to-br from-white/40 via-transparent to-transparent opacity-60 pointer-events-none"></span>
                  </Link>
                  
                  <Link to="/portfolio">
                    <button 
                      className="group relative flex items-center space-x-3 text-white px-6 py-3 rounded-full transition-all duration-300 overflow-hidden whitespace-nowrap"
                      style={{
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)'
                      }}
                    >
                      {/* Background that appears on hover */}
                      <span 
                        className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300"
                        style={{
                          background: 'white',
                          boxShadow: '0 8px 24px rgba(255, 255, 255, 0.3), inset -2px -2px 8px rgba(0, 0, 0, 0.1), inset 2px 2px 8px rgba(255, 255, 255, 0.8)'
                        }}
                      ></span>
                      
                      {/* Icon container */}
                      <div 
                        className="relative z-10 w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                        style={{
                          background: 'rgba(255, 255, 255, 0.2)',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2), inset -2px -2px 6px rgba(0, 0, 0, 0.2), inset 2px 2px 6px rgba(255, 255, 255, 0.3)'
                        }}
                      >
                        <FiPlay className="w-6 h-6 ml-1 text-white group-hover:text-primary-600 transition-colors duration-300" />
                      </div>
                      
                      {/* Text */}
                      <span className="relative z-10 font-medium text-lg text-white group-hover:text-primary-600 transition-colors duration-300">
                        Watch Our Work
                      </span>
                      
                      {/* 3D shine effect on hover */}
                      <span className="absolute inset-0 rounded-full bg-gradient-to-br from-white/40 via-transparent to-transparent opacity-0 group-hover:opacity-60 pointer-events-none transition-opacity duration-300"></span>
                    </button>
                  </Link>
                </motion.div>

                {/* Stats Bar */}
                <motion.div 
                  className="grid grid-cols-3 gap-4 mt-8 pt-6 border-t border-white/20"
                  variants={fadeInUp}
                  transition={{ ...fadeInUpTransition, delay: 0.4 }}
                >
                  <div className="text-center">
                    <div className="text-2xl md:text-3xl font-bold text-white">500+</div>
                    <div className="text-orange-200 text-xs md:text-sm font-medium">Projects Done</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl md:text-3xl font-bold text-white">99%</div>
                    <div className="text-orange-200 text-xs md:text-sm font-medium">Happy Clients</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl md:text-3xl font-bold text-white">7+</div>
                    <div className="text-orange-200 text-xs md:text-sm font-medium">Years Experience</div>
                  </div>
                </motion.div>
              </div>

              {/* Right Content - Visual Elements */}
              <motion.div 
                className="relative hidden lg:block"
                variants={fadeInUp}
                transition={{ ...fadeInUpTransition, delay: 0.2 }}
              >
                <div className="relative">
                  {/* Main Visual Element */}
                  <div className="relative bg-gradient-to-br from-white/10 to-white/5 rounded-2xl p-6 backdrop-blur-sm border border-white/20">
                    {/* Mock Dashboard/Analytics */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                        <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                        <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                      </div>
                      
                      <div className="bg-white/10 rounded-lg p-3 space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-orange-200 text-xs">Monthly Growth</span>
                          <span className="text-green-400 text-xs font-semibold">↗ 156%</span>
                        </div>
                        <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-green-400 to-blue-400 rounded-full animate-pulse" style={{width: '85%'}}></div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-white/10 rounded-lg p-2 text-center">
                          <div className="text-xl font-bold text-white">2.4M</div>
                          <div className="text-orange-200 text-xs">Reach</div>
                        </div>
                        <div className="bg-white/10 rounded-lg p-2 text-center">
                          <div className="text-xl font-bold text-white">89K</div>
                          <div className="text-orange-200 text-xs">Engagement</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Floating Elements */}
                  <div className="absolute -top-4 -right-4 w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full opacity-90 animate-bounce"></div>
                  <div className="absolute -bottom-3 -left-3 w-12 h-12 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full opacity-80 animate-pulse"></div>
                  <div className="absolute top-1/2 -right-6 w-10 h-10 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-full opacity-70 animate-ping"></div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trusted Brands Marquee Section */}
      <section className="py-12 bg-gray-50 border-y border-gray-100 overflow-hidden">
        <div className="container-width mb-8 text-center">
          <p className="text-gray-500 text-sm font-medium uppercase tracking-wider">Trusted By</p>
        </div>
        <div className="relative">
          {/* Gradient overlays for fade effect */}
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-gray-50 to-transparent z-10"></div>
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-gray-50 to-transparent z-10"></div>
          
          {/* Scrolling logos */}
          <div className="flex animate-marquee gap-16 items-center">
            {/* First set of logos */}
            <div className="flex gap-16 items-center min-w-max">
              <img src="/images/clients/little.png" alt="Little Italy" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/jagdish.png" alt="Jagdish" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/jpk9.png" alt="JPK9 Academy" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/mantavyaa.png" alt="Mantavyaa" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/vzstudio.png" alt="VZ Studio" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/topcrew.png" alt="Top Crew Aviation" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/oneleathercraft.png" alt="One Leather Craft" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/myfragrance.png" alt="My Fragrance Samples" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/mk-international.png" alt="MK International" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/dumpsters.png" alt="Dumpsters Direct" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/dr-nisharg.png" alt="Dr. Nisharg Patel" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/die.png" alt="DIE" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/agile-ortho.png" alt="Agile Ortho" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/belsa.png" alt="B'ELSA Herbals" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/afzaan.png" alt="Afzaan House" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
            </div>
            {/* Duplicate set for seamless loop */}
            <div className="flex gap-16 items-center min-w-max">
              <img src="/images/clients/little.png" alt="Little Italy" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/jagdish.png" alt="Jagdish" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/jpk9.png" alt="JPK9 Academy" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/mantavyaa.png" alt="Mantavyaa" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/vzstudio.png" alt="VZ Studio" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/topcrew.png" alt="Top Crew Aviation" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/oneleathercraft.png" alt="One Leather Craft" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/myfragrance.png" alt="My Fragrance Samples" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/mk-international.png" alt="MK International" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/dumpsters.png" alt="Dumpsters Direct" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/dr-nisharg.png" alt="Dr. Nisharg Patel" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/die.png" alt="DIE" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/agile-ortho.png" alt="Agile Ortho" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/belsa.png" alt="B'ELSA Herbals" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
              <img src="/images/clients/afzaan.png" alt="Afzaan House" className="h-12 object-contain opacity-60 hover:opacity-100 transition-opacity grayscale hover:grayscale-0" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - WAM Style */}
      <section className="py-20 bg-white border-t border-gray-100">
        <div className="container-width">
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {stats.map((stat, index) => (
              <Link to="/contact" key={index}>
                <motion.div
                  className="text-center group cursor-pointer"
                  variants={fadeInUp}
                  transition={fadeInUpTransition}
                >
                  <div className="mb-6 flex justify-center">
                    <div className="relative w-20 h-20 rounded-2xl flex items-center justify-center transition-all duration-300 shadow-lg group-hover:shadow-xl group-hover:scale-110 bg-[#52658C] group-hover:bg-[#F58634]">
                      <stat.icon className="w-10 h-10 text-white" />
                      <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full animate-pulse bg-[#6b7fa8]"></div>
                    </div>
                  </div>
                  <div className="text-4xl md:text-5xl font-bold text-gray-800 mb-3 group-hover:text-primary-600 transition-colors duration-300">
                    {stat.number}
                  </div>
                  <div className="text-gray-600 font-medium text-lg">
                    {stat.label}
                  </div>
                </motion.div>
              </Link>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-20 bg-white">
        <div className="container-width">
          {/* Content - Full Width */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center"
          >
            <div className="inline-block px-4 py-2 bg-primary-100 text-primary-600 rounded-full text-sm font-semibold mb-4">
              WHO WE ARE
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-800 mb-6">
              We Are a Digital Marketing Company{" "}
              <span className="text-primary-600">Focused on Performance and Long-Term Growth?</span>
            </h2>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Image */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              {/* Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop" 
                  alt="Team collaboration"
                  className="w-full h-[400px] object-cover"
                />
                
                {/* 7 Years Badge */}
                <motion.div 
                  className="absolute bottom-8 left-1/2 transform -translate-x-1/2 rounded-2xl p-6 text-white"
                  style={{
                    background: 'linear-gradient(135deg, #3e5277 0%, #3e5277 100%)',
                    boxShadow: '0 20px 60px rgba(62, 82, 119, 0.4), inset -5px -5px 15px rgba(0, 0, 0, 0.2), inset 5px 5px 15px rgba(255, 255, 255, 0.2)'
                  }}
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                      <span className="text-2xl">🚀</span>
                    </div>
                    <div>
                      <div className="text-4xl font-bold">8+</div>
                      <div className="text-sm opacity-90">YEARS OF EXPERIENCE</div>
                    </div>
                  </div>
                  <p className="text-sm leading-relaxed opacity-95">
                    We recognize our primary asset is our client. We work with our clients as a partner.
                  </p>
                </motion.div>
              </div>
            </motion.div>

            {/* Right - Specializations */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-gray-700 font-semibold mb-4">We specialize in:</p>

              {/* Icon Points */}
              <div className="space-y-6 mb-8">
                {[
                  {
                    icon: "🔍",
                    title: "SEO to improve search ranking and organic traffic",
                    desc: "Drive more qualified visitors to your website through strategic search engine optimization."
                  },
                  {
                    icon: "📢",
                    title: "Google Ads and Meta Ads for quality leads and sales",
                    desc: "Performance-driven advertising campaigns that deliver measurable results and ROI."
                  },
                  {
                    icon: "📱",
                    title: "Social Media Marketing and content planning",
                    desc: "Build your brand presence and engage with your audience across all major platforms."
                  },
                  {
                    icon: "🎬",
                    title: "Video content strategy, reels editing, and video editing",
                    desc: "Strengthen brand storytelling with compelling video content that converts."
                  }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    className="flex space-x-4 group"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div 
                      className="flex-shrink-0 w-14 h-14 rounded-xl flex items-center justify-center text-2xl transition-all duration-300 group-hover:scale-110"
                      style={{
                        background: 'linear-gradient(135deg, #3e5277 0%, #3e5277 100%)',
                        boxShadow: '0 10px 30px rgba(62, 82, 119, 0.3), inset -3px -3px 10px rgba(0, 0, 0, 0.2), inset 3px 3px 10px rgba(255, 255, 255, 0.2)'
                      }}
                    >
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-800 mb-1 group-hover:text-primary-600 transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>

              <p className="text-gray-800 font-semibold text-lg">
                Our goal is simple: understand your business, plan the right approach, and deliver measurable growth.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="w-full max-w-none px-0">
          {/* Header - Centered */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="inline-block px-6 py-3 bg-primary-100 text-primary-600 rounded-full text-lg font-bold mb-4">
              WHY CHOOSE US?
            </div>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-16 items-start px-6 sm:px-8 lg:px-12">
            {/* Left - Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-5xl md:text-6xl font-bold text-gray-800 mb-6">
                Work with the Best{" "}
                <span className="text-primary-600">Digital Marketing Agency in India?</span>
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                Work with the best digital marketing agency in India. You'll get a team that cares about your growth. Our approach combines strong creative work, deep audience research, and smart media planning. We track important metrics like cost per lead, return on ad spend, and conversions. This ensures every campaign moves your business forward.
              </p>

              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Key Highlights:</h3>
                <ul className="space-y-3">
                  {[
                    "Performance-focused approach.",
                    "Honest and transparent reporting.",
                    "Global experience and market understanding.",
                    "Dedicated support and project communication."
                  ].map((item, index) => (
                    <li key={index} className="flex items-center space-x-3 text-gray-700">
                      <span className="text-primary-600">✓</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA on left */}
              <Link to="/contact">
                <motion.button
                  className="mt-2 inline-flex items-center justify-center px-8 py-4 bg-primary-600 text-white rounded-full font-semibold hover:bg-primary-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Get a Service Now
                </motion.button>
              </Link>
            </motion.div>

            {/* Right - Image with Icon Boxes */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative w-full"
            >
              {/* Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&h=600&fit=crop"
                  alt="IT Solutions"
                  className="w-full h-auto object-cover md:aspect-[16/10] lg:max-h-[700px]"
                />
                
                {/* Animated 3D Icon Boxes Overlay */}
                <motion.div 
                  className="absolute bottom-8 left-8 right-8 rounded-2xl p-6 backdrop-blur-md"
                  style={{
                    background: 'linear-gradient(135deg, rgba(62, 82, 119, 0.95) 0%, rgba(62, 82, 119, 0.95) 100%)',
                    boxShadow: '0 20px 60px rgba(62, 82, 119, 0.4), inset -5px -5px 15px rgba(0, 0, 0, 0.2), inset 5px 5px 15px rgba(255, 255, 255, 0.2)'
                  }}
                >
                  <div className="space-y-4">
                    {[
                      { icon: "🌐", title: "IoT-Enabled Solutions", desc: "Internet of Things (IoT) applications to enhance operational efficiency..." },
                      { icon: "♻️", title: "Sustainable IT Practices", desc: "Internet of Things (IoT) applications to enhance operational efficiency..." },
                      { icon: "🎯", title: "Dedicated Client Success", desc: "Internet of Things (IoT) applications to enhance operational efficiency..." }
                    ].map((item, index) => (
                      <motion.div
                        key={index}
                        className="flex items-start space-x-3 text-white"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.15 }}
                        whileHover={{ x: 5 }}
                      >
                        <div 
                          className="flex-shrink-0 w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm"
                          style={{
                            boxShadow: 'inset -2px -2px 8px rgba(0, 0, 0, 0.2), inset 2px 2px 8px rgba(255, 255, 255, 0.2)'
                          }}
                        >
                          <span className="text-xl">{item.icon}</span>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-sm mb-1">{item.title}</h4>
                          <p className="text-xs opacity-90 leading-relaxed">{item.desc}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </div>

              {/* Progress Bars under image */}
              <div className="space-y-6 mt-8">
                {[
                  { label: "Data-led and Conversion-driven Marketing", value: 95, color: "#3e5277" },
                  { label: "SEO Experts with Proven Results", value: 90, color: "#5a6f99" },
                  { label: "Search + Social + Video + Funnel Growth", value: 88, color: "#7a8eb3" }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-semibold text-gray-800">{item.label}</span>
                      <span className="font-bold text-primary-600"><AnimatedNumber value={item.value} /></span>
                    </div>
                    <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full rounded-full"
                        style={{ backgroundColor: item.color }}
                        initial={{ width: 0 }}
                        whileInView={{ width: `${item.value}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.4, ease: 'easeOut', delay: index * 0.1 }}
                      />
                      {/* Loading shimmer */}
                      <motion.div
                        className="absolute inset-0 rounded-full"
                        style={{
                          background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.4) 50%, rgba(255,255,255,0) 100%)'
                        }}
                        initial={{ x: '-100%' }}
                        animate={{ x: '100%' }}
                        transition={{ duration: 1.4, repeat: Infinity, ease: 'linear', delay: 0.2 + index * 0.05 }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section - WAM Style */}
      <section id="services" className="py-20 bg-gray-900/20 relative overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 opacity-70 pointer-events-none z-0 hidden md:block">
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="none"
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 min-w-full min-h-full object-cover"
            style={{ filter: 'grayscale(0%) brightness(1.3)' }}
          >
            <source src="/videos/earth-globe.mp4" type="video/mp4" />
          </video>
        </div>

        {/* Floating Particles */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-primary-400 rounded-full opacity-20"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -30, 0],
                opacity: [0.2, 0.5, 0.2],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
                ease: "easeInOut",
                repeatType: "loop"
              }}
            />
          ))}
        </div>

        <div className="container-width relative z-10">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-block px-4 py-2 bg-primary-100 text-primary-600 rounded-full text-sm font-semibold mb-4">
              What We Do
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Our <span className="text-primary-600">Services</span>
            </h2>
            <p className="text-xl md:text-2xl font-semibold text-white max-w-3xl mx-auto drop-shadow-lg" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
              Complete digital marketing solutions focused on driving real results for your business.
            </p>
          </motion.div>

          {/* Horizontal Scrollable Container with Arrows */}
          <div className="relative flex items-center justify-center overflow-visible px-16">
            {/* Left Arrow */}
            <button
              onClick={() => {
                const container = document.getElementById('services-scroll-container');
                if (container) {
                  const card = container.querySelector('[data-service-card]') as HTMLElement | null;
                  const step = (card?.offsetWidth || 380) + 24; // card width + gap (gap-6 ≈ 24px)
                  container.scrollBy({ left: -step, behavior: 'smooth' });
                }
              }}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-20 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center hover:bg-primary-600 hover:text-white transition-all duration-300 group"
              style={{
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
              }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Right Arrow */}
            <button
              onClick={() => {
                const container = document.getElementById('services-scroll-container');
                if (container) {
                  const card = container.querySelector('[data-service-card]') as HTMLElement | null;
                  const step = (card?.offsetWidth || 380) + 24; // card width + gap (gap-6 ≈ 24px)
                  container.scrollBy({ left: step, behavior: 'smooth' });
                }
              }}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-20 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center hover:bg-primary-600 hover:text-white transition-all duration-300 group"
              style={{
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
              }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            {/* Scrollable Container */}
            <div
              id="services-scroll-container"
              className="flex gap-5 overflow-x-auto scrollbar-hide scroll-smooth snap-x snap-mandatory px-1 py-8"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
            >
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  className="group relative bg-white rounded-2xl p-8 transition-all duration-300 border border-gray-100 cursor-pointer flex-shrink-0 snap-start"
                  style={{
                    width: '365px',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08), inset -2px -2px 8px rgba(0, 0, 0, 0.05), inset 2px 2px 8px rgba(255, 255, 255, 0.8)',
                    scrollSnapAlign: 'start',
                  }}
                  data-service-card
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  whileHover={{
                    scale: 1.05,
                    y: -10,
                    boxShadow: '0 25px 70px rgba(82, 101, 140, 0.25), inset -3px -3px 12px rgba(0, 0, 0, 0.08), inset 3px 3px 12px rgba(255, 255, 255, 0.9)',
                  }}
                >
                  {/* Glossy Overlay */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>

                  {/* Service Icon/Number */}
                  <div className="relative mb-6">
                    <div 
                      className="w-16 h-16 rounded-2xl flex items-center justify-center text-white text-2xl font-bold transition-all duration-300 group-hover:scale-110 relative"
                      style={{
                        background: 'linear-gradient(135deg, #F58634 0%, #ff9d5c 100%)',
                        boxShadow: '0 8px 24px rgba(245, 134, 52, 0.4), inset -2px -2px 6px rgba(0, 0, 0, 0.2), inset 2px 2px 6px rgba(255, 255, 255, 0.3)',
                      }}
                    >
                      <span className="relative z-10">{String(index + 1).padStart(2, '0')}</span>
                      {/* Shine effect */}
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/30 via-transparent to-transparent opacity-60"></div>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-gray-800 mb-4 group-hover:!text-[#52658C] transition-colors duration-300 relative z-10">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed line-clamp-3 relative z-10">
                    {service.description}
                  </p>
                  
                  {/* Features List - Compact */}
                  <ul className="space-y-2 mb-6 text-sm relative z-10">
                    {service.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-center space-x-2 text-gray-600">
                        <div 
                          className="w-2 h-2 rounded-full"
                          style={{
                            background: 'linear-gradient(135deg, #F58634 0%, #ff9d5c 100%)',
                            boxShadow: '0 2px 6px rgba(245, 134, 52, 0.4)',
                          }}
                        ></div>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Link
                    to={service.href}
                    className="inline-flex items-center space-x-2 font-semibold group-hover:translate-x-2 transition-all duration-300 text-primary-600 group-hover:!text-[#F58634] relative z-10"
                  >
                    <span>Learn More</span>
                    <FiArrowRight className="w-4 h-4" />
                  </Link>

                  {/* 3D Background Gradient */}
                  <div 
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl pointer-events-none"
                    style={{ 
                      background: 'linear-gradient(135deg, rgba(82, 101, 140, 0.03) 0%, rgba(245, 134, 52, 0.02) 100%)',
                    }}
                  ></div>
                  
                  {/* Corner Accent */}
                  <div className="absolute bottom-0 right-0 w-24 h-24 rounded-tl-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{
                      background: 'radial-gradient(circle at bottom right, rgba(245, 134, 52, 0.1) 0%, transparent 70%)',
                    }}
                  ></div>
                </motion.div>
              ))}
            </div>
          </div>

          <style>{`
            .scrollbar-hide::-webkit-scrollbar {
              display: none;
            }
          `}</style>

          {/* Call-to-Action */}
          <motion.div 
            className="text-center mt-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Link
              to="/services"
              className="inline-flex items-center space-x-3 bg-primary-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-primary-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <span>View All Services</span>
              <FiArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section - WAM Style */}
      <section className="section-padding bg-white">
        <div className="container-width">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-block px-4 py-2 bg-primary-100 text-primary-600 rounded-full text-sm font-semibold mb-4">
              Testimonials
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              What Our <span className="text-primary-600">Clients</span> Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real feedback and stories from founders and marketing teams sharing how their business grew while working with us.

            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                className="bg-gray-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300"
                variants={fadeInUp}
                transition={fadeInUpTransition}
                style={{ willChange: 'box-shadow' }}
              >
                {/* Quote Icon */}
                <div className="flex justify-center mb-4">
                  <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center text-white text-2xl font-serif">
                    "
                  </div>
                </div>
                
                {/* Rating Stars */}
                <div className="flex justify-center mb-6 space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <FiStar key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                
                <p className="text-gray-700 mb-8 italic leading-relaxed text-center font-medium">
                  "{testimonial.quote}"
                </p>
                
                <div className="text-center pt-6">
                  <div className="mb-4 flex justify-center">
                    <img
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-14 h-14 rounded-full object-cover shadow-md"
                      loading="lazy"
                    />
                  </div>
                  <div className="font-semibold text-gray-800 mb-1">
                    {testimonial.name}
                  </div>
                  <div className="text-primary-600 font-medium text-sm">
                    {testimonial.company}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section - WAM Style */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 bg-white rounded-full"></div>
          <div className="absolute bottom-10 right-10 w-32 h-32 bg-white rounded-full"></div>
          <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-white rounded-full opacity-50"></div>
        </div>

        <div className="relative container-width section-padding">
          <motion.div 
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-block px-6 py-3 bg-white/10 rounded-full text-sm font-semibold mb-6 backdrop-blur-sm">
              🚀 Ready to Transform Your Business?
            </div>

            <h2 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Let's Build Something 
              <span className="block bg-gradient-to-r from-white to-orange-200 bg-clip-text text-transparent">
                Amazing Together
              </span>
            </h2>
            <p className="text-xl md:text-2xl text-orange-100 mb-10 leading-relaxed max-w-3xl mx-auto">
              Join hundreds of successful businesses that trust Mirani Marketing to drive their digital growth. 
              Your success story starts here.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
              <Link
                to="/contact"
                className="bg-white text-primary-600 px-10 py-5 rounded-full text-lg font-bold hover:bg-orange-50 transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl"
              >
                Start Your Journey
                <FiArrowRight className="ml-3 w-5 h-5 inline" />
              </Link>
              
              <Link
                to="/services"
                className="border-2 border-white text-white px-10 py-5 rounded-full text-lg font-bold hover:bg-white/10 transition-all duration-300"
              >
                Explore Services
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16 pt-10 border-t border-white/20">
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">500+ Projects</div>
                <div className="text-orange-200">Successfully Delivered</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">99% Satisfaction</div>
                <div className="text-orange-200">Client Happiness Rate</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-white mb-2">24/7 Support</div>
                <div className="text-orange-200">Always Here for You</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};